import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  EmailTrainingFirstFillExample,
  EmailTrainingLifecycle,
  EmailTrainingRequest,
  EmailTrainingResponse,
  EmailTrainingSource,
} from "@/types/adminV2/messagingEmailTraining";

const countTag = { type: "AdminV2MessagingTemplates", id: "EMAIL_TRAINING" };
const firstFillTag = {
  type: "AdminV2MessagingTemplates",
  id: "EMAIL_TRAINING_FIRST_FILL",
};

/** Training has an explicit write boundary; Save and Publish never call it. */
export const buildAdminV2MessagingEmailTrainingEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2MessagingEmailTrainingCount: builder.query<
    EmailTrainingLifecycle,
    EmailTrainingSource & { ownerKey: string }
  >({
    query: ({ templateId, versionId, organisationId }) => ({
      url: "/admin/v2/messaging/email-training",
      params: {
        templateId,
        versionId,
        ...(organisationId ? { organisationId } : {}),
      },
    }),
    providesTags: [countTag],
    keepUnusedDataFor: 0,
  }),
  trainAdminV2MessagingEmailExample: builder.mutation<
    EmailTrainingResponse,
    EmailTrainingRequest
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/email-training",
      method: "POST",
      data: body,
    }),
    // A rejection never increments or refreshes a count as if training succeeded.
    invalidatesTags: (result) =>
      result?.status === "stored" ||
      result?.status === "already_trained" ||
      result?.status === "excluded" ||
      result?.status === "included"
        ? [countTag]
        : [],
  }),
  // EA-21 / PD-17: staff review of the one-time first fill, one organisation at a time.
  getAdminV2MessagingEmailTrainingFirstFillExamples: builder.query<
    { examples: EmailTrainingFirstFillExample[] },
    { organisationId: string; ownerKey: string }
  >({
    query: ({ organisationId }) => ({
      url: "/admin/v2/messaging/email-training/first-fill",
      params: { organisationId },
    }),
    providesTags: [firstFillTag],
    keepUnusedDataFor: 0,
  }),
  withdrawAdminV2MessagingEmailTrainingFirstFillExample: builder.mutation<
    void,
    { exampleId: string; organisationId: string }
  >({
    query: ({ exampleId, organisationId }) => ({
      url: `/admin/v2/messaging/email-training/first-fill/${encodeURIComponent(exampleId)}/withdraw`,
      method: "POST",
      data: { organisationId },
    }),
    // A withdrawn example leaves the eligible set, so learning counts refresh too.
    invalidatesTags: (_result, error) =>
      error ? [] : [firstFillTag, countTag],
  }),
});
